import { DATA_TYPE_VALUES } from "@/constants/dataset";
import {
  DataSchemaType,
  DatasetAttributeSchema,
  DatasetDataType,
  DatasetDetailSchemaResponse
} from "@/types/dataset";

type ScoringAttribute = {
  id: string;
  paired_key_attribute_id?: string | null;
  position?: number | null;
};

/**
 * Returns true when an attribute's data_type is INT or FLOAT.
 * Used to gate scoring/weighting features that only apply to numeric data.
 *
 * Accepts both `DatasetAttributeSchema` (typed `DataSchemaType`) and
 * `DatasetDetailSchemaResponse` (typed `string`) shapes.
 */
export const isNumericAttribute = (attribute: {
  data_type: DataSchemaType | string;
}): boolean =>
  attribute.data_type === DATA_TYPE_VALUES[DatasetDataType.TYPE_INTEGER] ||
  attribute.data_type === DATA_TYPE_VALUES[DatasetDataType.TYPE_FLOAT];

export const getDatasetAttributeScoringId = (
  attribute: ScoringAttribute
): string => attribute.paired_key_attribute_id || attribute.id;
export const getPrimaryPoiAttribute = <T extends ScoringAttribute>(
  attributes: readonly T[]
): T | undefined =>
  attributes.reduce<T | undefined>((primary, attribute) => {
    if (!primary) return attribute;

    const primaryPosition =
      typeof primary.position === "number"
        ? primary.position
        : Number.POSITIVE_INFINITY;

    const attributePosition =
      typeof attribute.position === "number"
        ? attribute.position
        : Number.POSITIVE_INFINITY;

    return attributePosition < primaryPosition ? attribute : primary;
  }, undefined);

/**
 * Resolves the attribute the POI count component is keyed by. Counting the
 * reference-id column is counting the points, so that attribute anchors the
 * count and is never offered as a numeric weight component.
 *
 * Falls back to an attribute literally named `id`, then to the primary POI
 * attribute, so a schema without a reference id still resolves an anchor.
 */
export const getPoiCountAttribute = <T extends DatasetDetailSchemaResponse>(
  attributes: readonly T[]
): T | undefined =>
  attributes.find((attribute) => attribute.is_reference_id) ??
  attributes.find((attribute) => attribute.attribute_name === "id") ??
  getPrimaryPoiAttribute(attributes);

/**
 * Filters dataset categories to return only valid numeric attributes.
 * Valid attributes have INT or FLOAT data types and are not identifiers.
 *
 * @param categories - Array of dataset attribute schemas to filter
 * @returns Array of valid numeric attributes
 *
 * @example
 * const validAttrs = getValidNumericAttributes(dataset.categories);
 * console.log(validAttrs.length); // Number of valid attributes for weighting
 */
export const getValidNumericAttributes = (
  categories: DatasetAttributeSchema[]
): DatasetAttributeSchema[] => {
  return categories.filter(
    (category) =>
      isNumericAttribute(category) &&
      category.attribute_name !== "id" &&
      !category.is_reference_id
  );
};

/**
 * Ordered weight components of a POI dataset: the count anchor first, then
 * every numeric attribute. Non-numeric attributes other than the anchor carry
 * no weight and are left out.
 *
 * A paired attribute arrives as two schema rows sharing one scoring id, so it
 * yields a single component — the row holding the display label.
 */
export const getPoiWeightableAttributes = (
  categories: DatasetAttributeSchema[]
): DatasetAttributeSchema[] => {
  const countAttribute = getPoiCountAttribute(categories);
  const numericAttributes = getValidNumericAttributes(categories).filter(
    (attribute) => attribute.id !== countAttribute?.id
  );
  const weightable = countAttribute
    ? [countAttribute, ...numericAttributes]
    : numericAttributes;

  const byScoringId = new Map<string, DatasetAttributeSchema>();
  for (const attribute of weightable) {
    const scoringId = getDatasetAttributeScoringId(attribute);
    const existing = byScoringId.get(scoringId);
    // The paired row carries the clean label ("Revenue", not "Revenue Code").
    if (!existing || attribute.paired_key_attribute_id) {
      byScoringId.set(scoringId, attribute);
    }
  }

  return Array.from(byScoringId.values());
};

/**
 * Gets the count of valid numeric attributes in a dataset.
 * This is a convenience wrapper around getValidNumericAttributes that returns only the count.
 *
 * @param categories - Array of dataset attribute schemas
 * @returns Number of valid numeric attributes
 *
 * @example
 * const count = getValidNumericAttributeCount(dataset.categories);
 * console.log(count); // e.g., 5
 */
export const getValidNumericAttributeCount = (
  categories: DatasetAttributeSchema[]
): number => {
  return getValidNumericAttributes(categories).length;
};

/** True when a timeseries dataset has a Group By (x-axis) attribute set. */
export const isTimeseriesWithGroupBy = (
  timeseries?: boolean,
  groupBy?: string | null
): boolean => Boolean(timeseries && groupBy && groupBy !== "none");

/**
 * The Fill column a timeseries chart uses as its y-axis: the current one when
 * it is a valid numeric option, otherwise the first visible numeric attribute.
 */
export const getTimeseriesFillCategory = (
  categories: DatasetAttributeSchema[],
  currentAttributeName?: string
): DatasetAttributeSchema | undefined => {
  const numericCategories = getValidNumericAttributes(
    categories.filter((category) => category.is_visible)
  );
  return (
    numericCategories.find(
      (category) => category.attribute_name === currentAttributeName
    ) ?? numericCategories[0]
  );
};

/** Fill Column Option choices: numeric only while the timeseries chart uses Fill as its y-axis. */
export const getFillColumnOptions = (
  categories: DatasetAttributeSchema[],
  timeseries?: boolean,
  groupBy?: string | null
): DatasetAttributeSchema[] =>
  isTimeseriesWithGroupBy(timeseries, groupBy)
    ? getValidNumericAttributes(categories)
    : categories;
